// components/layout/Header.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Menu, Search, Bell, Plus, Settings, User, LogOut,
    ChevronDown, Globe, X,
    MessageSquare, Filter, Download, Calendar,
    Zap, Shield, Clock
} from 'lucide-react';
import { Button, Badge, Card } from '../ui';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
import Logo from '../../assets/logo.png'; // Import your logo

const Header = ({ setSidebarOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth(); // Get logout function and user from context
    const [searchQuery, setSearchQuery] = useState('');
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const searchRef = useRef(null);
    const mobileSearchRef = useRef(null);
    const notificationRef = useRef(null);
    const profileRef = useRef(null);

    // Mock notifications data
    const notifications = [
        {
            id: 1,
            type: 'warning',
            title: 'Delivery Delayed',
            message: 'Job JOB003 is delayed by 2 hours due to traffic',
            time: '5 mins ago',
            unread: true
        },
        {
            id: 2,
            type: 'success',
            title: 'Job Completed',
            message: 'Container CONT789 delivered successfully',
            time: '1 hour ago',
            unread: true
        },
        {
            id: 3,
            type: 'info',
            title: 'New Job Assigned',
            message: 'Job JOB005 assigned to vehicle MH-01-AB-1234',
            time: '2 hours ago',
            unread: false
        },
        {
            id: 4,
            type: 'error',
            title: 'GPS Offline',
            message: 'Vehicle MH-02-CD-5678 GPS signal lost',
            time: '3 hours ago',
            unread: false
        }
    ];

    const unreadCount = notifications.filter(n => n.unread).length;

    // Quick Actions
    const quickActions = [
        { icon: Plus, label: 'New Job', action: () => navigate('/jobs/create'), color: 'blue' },
        { icon: Calendar, label: 'Schedule', action: () => { }, color: 'green' },
        { icon: Download, label: 'Reports', action: () => navigate('/reports'), color: 'purple' },
        { icon: Filter, label: 'Filters', action: () => { }, color: 'orange' }
    ];

    // Handle logout
    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            const result = await logout();
            if (result.success) {
                setShowProfile(false);
                navigate('/login');
            } else {
                console.error('Logout failed:', result.error);
                // You could show an error message here
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    // Close dropdowns when clicking outside or pressing escape
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setShowProfile(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowNotifications(false);
                setShowProfile(false);
                setIsMobileSearchOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // Auto-focus search when mobile search opens
    useEffect(() => {
        if (isMobileSearchOpen && mobileSearchRef.current) {
            mobileSearchRef.current.focus();
        }
    }, [isMobileSearchOpen]);

    // Search functionality
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/jobs?search=${encodeURIComponent(searchQuery.trim())}`);
            setIsMobileSearchOpen(false);
        }
    };

    // Clear search
    const clearSearch = () => {
        setSearchQuery('');
        if (searchRef.current) {
            searchRef.current.focus();
        }
    };

    // Get page title based on current route
    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/' || path === '/dashboard') return 'Dashboard';
        if (path.startsWith('/jobs')) {
            if (path.includes('/create')) return 'Create New Job';
            if (path.includes('/edit')) return 'Edit Job';
            return 'Jobs Management';
        }
        if (path.startsWith('/tracking')) return 'Live Tracking';
        if (path.startsWith('/reports')) return 'Reports & Analytics';
        if (path.startsWith('/masters')) return 'Master Data';
        if (path.startsWith('/settings')) return 'Settings';
        return 'TrackTrace';
    };

    // Get user initials for avatar
    const getUserInitials = () => {
        if (user?.name) {
            return user.name.split(' ').map(n => n[0]).join('').toUpperCase();
        }
        return 'U';
    };

    const NotificationItem = ({ notification }) => {
        const icons = {
            warning: { icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
            success: { icon: Zap, color: 'text-green-600 bg-green-50' },
            info: { icon: Bell, color: 'text-blue-600 bg-blue-50' },
            error: { icon: Shield, color: 'text-red-600 bg-red-50' }
        };

        const config = icons[notification.type];
        const IconComponent = config.icon;

        return (
            <button
                className={`w-full p-4 hover:bg-gray-50 transition-colors border-l-4 text-left ${notification.unread ? 'border-blue-500 bg-blue-50/30' : 'border-transparent'
                    }`}
                onClick={() => setShowNotifications(false)}
            >
                <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${config.color}`}>
                        <IconComponent className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                            <p className={`text-sm font-medium truncate ${notification.unread ? 'text-gray-900' : 'text-gray-700'
                                }`}>
                                {notification.title}
                            </p>
                            {notification.unread && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2" />
                            )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-2">{notification.time}</p>
                    </div>
                </div>
            </button>
        );
    };

    return (
        <>
            <header className="bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 sticky top-0 z-40">
                <div className="flex items-center justify-between h-16 px-4 sm:px-6">
                    {/* Left Section */}
                    <div className="flex items-center space-x-4 min-w-0 flex-1 lg:flex-initial">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg p-2 transition-colors flex-shrink-0"
                            aria-label="Open sidebar"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        {/* Mobile Search Button */}
                        <button
                            onClick={() => setIsMobileSearchOpen(true)}
                            className="lg:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg p-2 transition-colors flex-shrink-0"
                            aria-label="Open search"
                        >
                            <Search className="w-5 h-5" />
                        </button>

                        {/* Desktop Logo and Page Title */}
                        <div className="hidden lg:flex items-center space-x-3 min-w-0">
                            <img
                                src={Logo}
                                alt="TrackTrace Logo"
                                className="h-8 flex-shrink-0"
                            />
                            <div className="min-w-0">
                                <h1 className="text-xl font-semibold text-gray-900 truncate">{getPageTitle()}</h1>
                                <p className="text-sm text-gray-500 truncate">
                                    {new Date().toLocaleDateString('en-IN', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            </div>
                        </div>

                        {/* Mobile Logo and Page Title */}
                        <div className="lg:hidden flex items-center space-x-2 min-w-0 flex-1">
                            <img
                                src={Logo}
                                alt="TrackTrace Logo"
                                className="w-6 h-6 flex-shrink-0"
                            />
                            <h1 className="text-lg font-semibold text-gray-900 truncate">{getPageTitle()}</h1>
                        </div>
                    </div>

                    {/* Center Section - Desktop Search */}
                    <div className="hidden lg:flex items-center flex-1 max-w-2xl mx-8">
                        <form onSubmit={handleSearch} className="w-full">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400 w-5 h-5" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search jobs, containers, routes, customers..."
                                    className="w-full pl-12 pr-20 py-3 border border-gray-200 rounded-xl 
                                     focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                                     bg-gray-50/50 backdrop-blur-sm transition-all duration-200
                                     placeholder-gray-400 text-sm"
                                />
                                {searchQuery && (
                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                                        <button
                                            type="button"
                                            onClick={clearSearch}
                                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                                            aria-label="Clear search"
                                        >
                                            <X className="w-4 h-4 text-gray-400" />
                                        </button>
                                        <Button size="sm" variant="primary" type="submit">
                                            Search
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                        {/* Notifications */}
                        <div className="relative" ref={notificationRef}>
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                aria-label="Notifications"
                            >
                                <Bell className="w-5 h-5" />
                                {unreadCount > 0 && (
                                    <Badge
                                        variant="danger"
                                        size="sm"
                                        className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 flex items-center justify-center text-xs"
                                    >
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </Badge>
                                )}
                            </button>

                            {showNotifications && (
                                <div className="absolute right-0 top-12 w-80 sm:w-96 z-50">
                                    <Card className="max-h-96 overflow-hidden shadow-lg border" padding={false}>
                                        <div className="p-4 border-b border-gray-100 bg-gray-50">
                                            <div className="flex items-center justify-between">
                                                <h3 className="font-medium text-gray-900">Notifications</h3>
                                                <Badge variant="primary" size="sm">{unreadCount} new</Badge>
                                            </div>
                                        </div>

                                        <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                                            {notifications.length > 0 ? (
                                                notifications.map((notification) => (
                                                    <NotificationItem key={notification.id} notification={notification} />
                                                ))
                                            ) : (
                                                <div className="p-8 text-center text-gray-500">
                                                    <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                                    <p>No notifications</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-4 border-t border-gray-100 bg-gray-50">
                                            <Button variant="outline" size="sm" fullWidth onClick={() => setShowNotifications(false)}>
                                                View All Notifications
                                            </Button>
                                        </div>
                                    </Card>
                                </div>
                            )}
                        </div>

                        {/* Profile Dropdown */}
                        <div className="relative" ref={profileRef}>
                            <button
                                onClick={() => setShowProfile(!showProfile)}
                                className="flex items-center space-x-2 sm:space-x-3 p-1 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                aria-label="Profile menu"
                            >
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                                    <span className="text-white text-sm font-medium">{getUserInitials()}</span>
                                </div>
                                <div className="hidden sm:block text-left min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                                    <p className="text-xs text-gray-500 truncate">{user?.role || 'Operations Manager'}</p>
                                </div>
                                <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block flex-shrink-0" />
                            </button>

                            {showProfile && (
                                <div className="absolute right-0 top-12 w-64 z-50">
                                    <Card className="shadow-lg border" padding={false}>
                                        <div className="p-4 border-b border-gray-100">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-white font-medium">{getUserInitials()}</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                                                    <p className="text-sm text-gray-500 truncate">{user?.email || 'user@tracktrace.com'}</p>
                                                    <Badge variant="success" size="sm" className="mt-1">Active</Badge>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-2">
                                            {[
                                                { icon: User, label: 'Profile Settings', action: () => navigate('/profile') },
                                                { icon: Settings, label: 'Preferences', action: () => navigate('/settings') },
                                                { icon: MessageSquare, label: 'Support', action: () => { } },
                                                { icon: Globe, label: 'Language', action: () => { } }
                                            ].map((item, index) => {
                                                const IconComponent = item.icon;
                                                return (
                                                    <button
                                                        key={index}
                                                        onClick={() => {
                                                            item.action();
                                                            setShowProfile(false);
                                                        }}
                                                        className="w-full flex items-center px-3 py-2 text-left rounded-lg hover:bg-gray-100 transition-colors"
                                                    >
                                                        <IconComponent className="w-4 h-4 mr-3 text-gray-500 flex-shrink-0" />
                                                        <span className="text-sm text-gray-700">{item.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <div className="p-2 border-t border-gray-100">
                                            <button
                                                onClick={handleLogout}
                                                disabled={isLoggingOut}
                                                className="w-full flex items-center px-3 py-2 text-left rounded-lg hover:bg-red-50 transition-colors text-red-600 disabled:opacity-50"
                                            >
                                                <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                                                <span className="text-sm">
                                                    {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
                                                </span>
                                            </button>
                                        </div>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Search Overlay */}
            {isMobileSearchOpen && (
                <div className="lg:hidden fixed inset-0 bg-white z-50">
                    <div className="flex items-center p-4 border-b border-gray-200">
                        <button
                            onClick={() => setIsMobileSearchOpen(false)}
                            className="mr-3 p-2 -ml-2 text-gray-600 hover:text-gray-900"
                            aria-label="Close search"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <form onSubmit={handleSearch} className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    ref={mobileSearchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search jobs, containers, routes..."
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;